<template>
    <div class="event-calendar-filter flex items-center gap-3">
        <span
            :class="todayButtonClassList"
            data-test="event-calendar-filter-today"
            @click="toggleToday"
        >
            {{ $t('eventCalendar.filter_today') }}</span>
        <span :class="currentMonthClassList">{{ currentMonth }}</span>
        <div class="event-calendar-filter__controls flex flex-nowrap gap-3">
            <IconSimpleArrowThin
                class="event-calendar-filter__controls__item rounded-full cursor-pointer"
                :custom-color="buttonColor"
                data-test="event-calendar-filter-prev"
                @click="changeMonth(Direction.Prev)"
            />
            <IconSimpleArrowThin
                class="event-calendar-filter__controls__item rounded-full cursor-pointer"
                :custom-color="buttonColor"
                data-test="event-calendar-filter-next"
                @click="changeMonth(Direction.Next)"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
    import { useVModels } from '@vueuse/core';
    import { computed, ref } from 'vue';
    import moment from 'moment';
    import IconSimpleArrowThin from '~/components/icons/IconSimpleArrowThin.vue';
    import { useAccountStore } from '~/stores/accountStore';

    type EventCalendarFilterProps = {
        from: string;
        to: string;
    }

    enum Direction {
        Prev = 'prev',
        Next = 'next',
    }

    const emit = defineEmits({
        'update:from': (value: string) => typeof value === 'string',
        'update:to': (value: string) => typeof value === 'string',
    });
    const props = defineProps<EventCalendarFilterProps>();

    const isTodayFilterActive = ref(false);
    const { from: fromLocal, to: toLocal } = useVModels(props, emit);
    const { buttonColor, buttonTextColor } = useAccountStore().state;

    const currentMonth = computed(() => moment(fromLocal.value).format('MMMM YYYY'));

    const todayButtonClassList = computed(() => [
        'event-calendar-filter__today w-auto btn cursor-pointer',
        { 'event-calendar-filter__today--active' : isTodayFilterActive.value }
    ]);

    const currentMonthClassList = computed(() => [
        'event-calendar-filter__month w-full text-lg font-bold truncate capitalize',
        { 'event-calendar-filter__month--active' : isTodayFilterActive.value },
    ]);

    const toggleToday = () => {
        isTodayFilterActive.value = !isTodayFilterActive.value;
        if (isTodayFilterActive.value) {
            fromLocal.value = moment().format('YYYY-MM-DD 00:00:00');
            return toLocal.value = moment().format('YYYY-MM-DD 23:59:59');
        }
        fromLocal.value = moment().startOf('month').format('YYYY-MM-DD 00:00:00');
        toLocal.value = moment().endOf('month').format('YYYY-MM-DD 23:59:59');
    };

    const changeMonth = (direction: Direction) => {
        isTodayFilterActive.value = false;
        const current = moment(fromLocal.value);
        const newDate = direction === Direction.Prev ? current.subtract(1, 'month') : current.add(1, 'month');
        fromLocal.value = newDate.startOf('month').format('YYYY-MM-DD 00:00:00');
        toLocal.value = newDate.endOf('month').format('YYYY-MM-DD 23:59:59');
    };
</script>

<style lang="scss" scoped>
.event-calendar-filter {
  &__today {
    border-color: v-bind(buttonColor);
    color: v-bind(buttonColor);
    background-color: transparent;
    &--active {
      background-color: v-bind(buttonColor);
      color: v-bind(buttonTextColor);
    }
  }
  &__month {
    text-align: right;
    transition: all 0.3s ease;
    opacity: 1;
    &--active {
      opacity: 0.25;
    }
    @media (min-width: theme('screens.sm')) {
      font-size: 1.25rem;
    }
  }
  &__controls {
    &__item {
      min-width: 35px;
      min-height: 35px;
      width: 35px;
      height: 35px;
      background-color: white;
      border: 11px solid white;
      &:first-child {
        transform: rotate(180deg);
      }
    }
  }
}
</style>
